import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DatabricksDashboard as DatabricksSDK } from '@databricks/aibi-client';
import api from '../api';
import './DatabricksDashboard.css';

// ─────────────────────────────────────────────────────────────────────────────

function DatabricksDashboard() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const dashboardId = searchParams.get('id');
  const dashboardName = searchParams.get('name') || 'Databricks Dashboard';

  const containerRef = useRef(null);
  const sdkInstanceRef = useRef(null);

  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [dashboardInfo, setDashboardInfo] = useState(null);

  // ── Token refresh callback (called by SDK when token is about to expire) ─────
  const getNewToken = useCallback(async () => {
    try {
      const res = await api.post(
        `/api/databricks-embed/refresh-token?dashboardId=${encodeURIComponent(dashboardId)}`
      );
      return res.data.embed_token;
    } catch (err) {
      console.error('[DatabricksDashboard] ❌ Token refresh failed:', err.message);
      throw err;
    }
  }, [dashboardId]);

  // ── Main initialisation ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!dashboardId) {
      setStatus('error');
      setErrorMsg('No dashboard ID specified in the URL.');
      return;
    }

    let cancelled = false;

    async function init() {
      try {
        setStatus('loading');

        // 1. Fetch embed config from our backend (includes minted JWT)
        const configRes = await api.get(
          `/api/databricks-embed/embed-config?dashboardId=${encodeURIComponent(dashboardId)}`
        );
        const embedConfig = configRes.data;

        if (!embedConfig.success) {
          throw new Error(embedConfig.error || 'Failed to get embed config');
        }

        if (cancelled) return;
        setDashboardInfo({ name: embedConfig.name || dashboardName });

        // Normalise instanceUrl — must be https://host with no trailing slash
        const instanceUrl = (embedConfig.databricksHost || '')
          .replace(/\/$/, '')
          .replace(/^(?!https?:\/\/)/, 'https://');

        const workspaceId = String(embedConfig.workspaceId || '');
        const dbDashboardId = embedConfig.dashboardId;
        const token = embedConfig.embed_token;

        if (!containerRef.current) {
          throw new Error('Dashboard container not mounted');
        }

        // 2. Instantiate SDK — it renders an iframe into the container
        const sdk = new DatabricksSDK({
          instanceUrl,
          workspaceId,
          container: containerRef.current,
          dashboardId: dbDashboardId,
          token,
          getNewToken,
          colorScheme: 'light dark'
        });

        sdk.initialize();
        sdkInstanceRef.current = sdk;

        if (!cancelled) {
          setStatus('ready');
        }
      } catch (err) {
        console.error('[DatabricksDashboard] Initialisation error:', err);
        if (!cancelled) {
          setStatus('error');
          setErrorMsg(
            err?.response?.data?.error ||
            err?.response?.data?.details ||
            err.message ||
            'Unknown error'
          );
        }
      }
    }

    init();

    return () => {
      cancelled = true;
      // Destroy SDK instance to clean up iframe + timers
      if (sdkInstanceRef.current && typeof sdkInstanceRef.current.destroy === 'function') {
        sdkInstanceRef.current.destroy();
      }
      sdkInstanceRef.current = null;
    };
  }, [dashboardId, dashboardName, getNewToken]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="db-dashboard-page">
      {/* Header */}
      <div className="db-dashboard-header">
        <button className="db-back-btn" onClick={() => navigate('/insights')} aria-label="Back">
          ← Back
        </button>
        <div className="db-dashboard-title-group">
          <span className="db-dashboard-badge">Databricks AI/BI</span>
          <h1 className="db-dashboard-title">
            {dashboardInfo?.name || dashboardName}
          </h1>
        </div>
      </div>

      {/* Loading state */}
      {status === 'loading' && (
        <div className="db-dashboard-overlay">
          <div className="db-spinner" aria-label="Loading dashboard" />
          <p className="db-status-text">Loading dashboard&hellip;</p>
        </div>
      )}

      {/* Error state */}
      {status === 'error' && (
        <div className="db-dashboard-overlay db-error">
          <div className="db-error-icon">⚠</div>
          <h2 className="db-error-title">Failed to load dashboard</h2>
          <p className="db-error-detail">{errorMsg}</p>
          <button
            className="db-retry-btn"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      )}

      {/* Embed container — always in DOM so the SDK can write into it */}
      <div
        ref={containerRef}
        className={`db-embed-container${status === 'ready' ? ' db-embed-visible' : ''}`}
        aria-label="Databricks Dashboard"
      />
    </div>
  );
}

export default DatabricksDashboard;
